// apps/web/src/pages/vendas/fechamento/fechamentos-anteriores.tsx — Fase 12, Plano 14 (FECH-02; UI-SPEC § Aba Fechamento).
// 'Fechamentos anteriores': fechamentos já feitos no PV, com o estado do rascunho e do faturamento (ESTADO_FISCAL_CONFIG
// existente), e a entrada para abrir a revisão do rascunho. Leitura com pv:ler.
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import type { FechamentoAnteriorPv } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { chaveEstadoFiscal, rotuloEstadoRascunho } from '../../../lib/vendas/fechamento';
import { formatarDataLocal } from '../../../lib/vendas/formatacao';
import { ESTADO_FISCAL_CONFIG } from '../../pcp/retorno-panel';

interface FechamentosAnterioresProps {
  readonly pedidoId: string;
  /** Muda quando algo foi gravado (novo fechamento, aprovação) para reler a lista. */
  readonly atualizacao: number;
  readonly onAbrirRevisao: (retornoId: string) => void;
}

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

const CLASSE_RASCUNHO: Record<FechamentoAnteriorPv['estadoRascunho'], string> = {
  PENDENTE: 'border-amber-300 bg-amber-100 text-amber-800',
  PRONTO: 'border-blue-300 bg-blue-100 text-blue-800',
  APROVADO: 'border-emerald-300 bg-emerald-100 text-emerald-800',
};

export function FechamentosAnteriores({ pedidoId, atualizacao, onAbrirRevisao }: FechamentosAnterioresProps) {
  const [fechamentos, setFechamentos] = useState<FechamentoAnteriorPv[] | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(
    async (sinal: { vivo: boolean }) => {
      setErro(false);
      try {
        const res = await fetch(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/fechamentos`, { credentials: 'include' });
        if (!sinal.vivo) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const dados = (await res.json()) as FechamentoAnteriorPv[];
        if (sinal.vivo) setFechamentos(dados);
      } catch {
        if (sinal.vivo) setErro(true);
      }
    },
    [pedidoId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar, atualizacao]);

  return (
    <section className="flex flex-col gap-2" aria-labelledby="fechamentos-anteriores-titulo">
      <h2 id="fechamentos-anteriores-titulo" className="text-base font-semibold">
        Fechamentos anteriores
      </h2>
      {erro ? (
        <Card>
          <div role="alert" className="flex flex-col items-center gap-3 px-6 py-8 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar os fechamentos anteriores.</p>
            <Button type="button" variant="outline" onClick={() => void carregar({ vivo: true })}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : fechamentos === null ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando fechamentos anteriores">
          <Skeleton className="h-10 w-full" />
        </div>
      ) : fechamentos.length === 0 ? (
        <Card>
          <p className="px-6 py-8 text-center text-sm text-muted-foreground">Nenhum fechamento registrado.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th scope="col" className={CABECALHO}>Retorno</th>
                <th scope="col" className={CABECALHO}>Data</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Lotes</th>
                <th scope="col" className={CABECALHO}>Rascunho</th>
                <th scope="col" className={CABECALHO}>Faturamento</th>
                <th scope="col" className={CABECALHO}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {fechamentos.map((fechamento) => {
                const chave = chaveEstadoFiscal(fechamento.faturamentoEstado);
                const faturamento = chave ? ESTADO_FISCAL_CONFIG[chave] : null;
                return (
                  <tr key={fechamento.retornoId} className="border-t">
                    <td className="px-3 py-2 font-medium tabular-nums">{fechamento.retornoRef}</td>
                    <td className="px-3 py-2 tabular-nums">{formatarDataLocal(fechamento.createdAt)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fechamento.totalLotes}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${CLASSE_RASCUNHO[fechamento.estadoRascunho]}`}>
                        {rotuloEstadoRascunho(fechamento.estadoRascunho)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {faturamento ? (
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${faturamento.corBadge}`}>{faturamento.label}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Retido até a aprovação</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button type="button" variant="outline" size="sm" onClick={() => onAbrirRevisao(fechamento.retornoId)}>
                        Revisar rascunho
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </section>
  );
}
